import React from 'react';
import { Input } from '@/components/ui/input';
import { formatMoney } from '@/lib/line-items';
import { materialOrderTotals, parseCharge } from './lib/material-order-math';

// the same boxes the item table's own totals use (design-system/forms.css)
const moneyInput = 'line-items-field font-form-input generic-form-field text-right aria-[invalid=true]:border-destructive';
const row = 'grid grid-cols-[1fr,6rem,6rem] items-center gap-3';
const selectAll = (event: React.FocusEvent<HTMLInputElement>) => event.currentTarget.select();

export interface MaterialOrderChargesValue {
  tax: string;
  other: string;
}

/**
 * What a material order adds to its items -- the same two charges as a purchase order: Tax (%) of the subtotal and Other ($, a fixed amount) -- and the Total.
 * The item table above shows the Subtotal; the arithmetic is lib/material-order-math.ts (the server repeats it and stores ITS total).
 */
export function MaterialOrderCharges({ subtotal, value, onChange, disabled }: { subtotal: number; value: MaterialOrderChargesValue; onChange: (next: MaterialOrderChargesValue) => void; disabled?: boolean }) {
  const tax = parseCharge(value.tax, 100);
  const other = parseCharge(value.other);
  const totals = materialOrderTotals(subtotal, tax ?? 0, other ?? 0);
  return (
    <div className="ml-auto w-full md:w-96 space-y-2 text-sm" data-testid="material-order-charges">
      <div className={row}>
        <label htmlFor="mo_tax" className="justify-self-end text-gray-600">Tax (%):</label>
        <Input id="mo_tax" inputMode="decimal" disabled={disabled} value={value.tax} placeholder="0" aria-invalid={tax === null || undefined} onChange={(e) => onChange({ ...value, tax: e.target.value })} onFocus={selectAll} className={moneyInput} />
        <span className="tabular-nums text-right" data-testid="totals-tax">{formatMoney(totals.tax)}</span>
      </div>
      <div className={row}>
        <label htmlFor="mo_other" className="justify-self-end text-gray-600">Other ($):</label>
        <Input id="mo_other" inputMode="decimal" disabled={disabled} value={value.other} placeholder="0.00" aria-invalid={other === null || undefined} onChange={(e) => onChange({ ...value, other: e.target.value })} onFocus={selectAll} className={moneyInput} />
        <span className="tabular-nums text-right" data-testid="totals-other">{formatMoney(totals.other)}</span>
      </div>
      <div className={`${row} border-t border-border pt-2 font-bold`}>
        <span className="justify-self-end">Total Order Cost:</span>
        <span />
        <span className="tabular-nums text-right" data-testid="totals-total">{formatMoney(totals.total)}</span>
      </div>
    </div>
  );
}

/** Why the charges cannot be saved, or null. */
export function chargesProblem(value: MaterialOrderChargesValue): string | null {
  if (parseCharge(value.tax, 100) === null) return 'Tax must be a percentage between 0 and 100.';
  if (parseCharge(value.other) === null) return 'Other must be an amount of 0 or more.';
  return null;
}
